import { Component } from '@theme/component';

const DESKTOP_QUERY = '(min-width: 750px)';

/**
 * Keeps the sticky product details column (title, price, options and the add to cart
 * button) in view on desktop while the gallery scrolls, until the end of the gallery
 * lines up with the add to cart button. From there the whole page scrolls together and
 * the rest of the details comes into view under the gallery.
 *
 * It measures how far the add to cart button sits from the top of the column and how much
 * content follows it, and exposes both as custom properties:
 * - `--product-details-buy-end` on this element, so the column sticks high enough for the
 *   button to stay visible on short screens.
 * - `--product-media-min-height` on the product grid: the gallery height plus the content
 *   after the button, so the media column (and the sticky range) ends with the button
 *   level with the end of the gallery. Content under the gallery fills that space.
 */
class StickyProductDetails extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {MutationObserver | undefined} */
  #mediaObserver;

  /** @type {MediaQueryList} */
  #desktopQuery = window.matchMedia(DESKTOP_QUERY);

  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {Element | null} */
  #gallery = null;

  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    // Quick Add shows the same details inside a scrolling dialog; keep its default sticky behavior.
    if (this.closest('dialog')) return;

    this.#grid = this.closest('.product-information__grid');
    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(this);

    this.#observeGallery();

    // Picking another color can swap the whole gallery (media-gallery.js); follow the new one.
    const media = this.#media;
    if (media) {
      this.#mediaObserver = new MutationObserver(this.#observeGallery);
      this.#mediaObserver.observe(media, { childList: true });
    }

    this.#desktopQuery.addEventListener('change', this.#scheduleUpdate);
    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#mediaObserver?.disconnect();
    this.#mediaObserver = undefined;
    this.#desktopQuery.removeEventListener('change', this.#scheduleUpdate);
    cancelAnimationFrame(this.#frame);
    this.#reset();
    this.#grid = null;
    this.#gallery = null;
  }

  /** @returns {HTMLElement | null | undefined} */
  get #media() {
    return this.#grid?.querySelector(':scope > .product-information__media');
  }

  #observeGallery = () => {
    // The media column also holds the gallery's scripts and styles; watch the gallery itself.
    const gallery = this.#media?.querySelector(':scope > :not(script, style, link)') ?? null;
    if (gallery === this.#gallery) return;

    if (this.#gallery) this.#resizeObserver?.unobserve(this.#gallery);
    this.#gallery = gallery;
    if (gallery) this.#resizeObserver?.observe(gallery);
    this.#scheduleUpdate();
  };

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const grid = this.#grid;
    const button = this.querySelector('.add-to-cart-button');

    if (!grid || !this.#gallery || !(button instanceof HTMLElement) || !this.#desktopQuery.matches) {
      this.#reset();
      return;
    }

    const buyEnd = button.getBoundingClientRect().bottom - this.getBoundingClientRect().top;
    const overhang = Math.max(0, this.offsetHeight - buyEnd);

    this.style.setProperty('--product-details-buy-end', `${Math.round(buyEnd)}px`);
    const mediaMinHeight = this.#gallery.getBoundingClientRect().height + overhang;
    grid.style.setProperty('--product-media-min-height', `${Math.round(mediaMinHeight)}px`);
  };

  #reset() {
    this.style.removeProperty('--product-details-buy-end');
    this.#grid?.style.removeProperty('--product-media-min-height');
  }
}

if (!customElements.get('sticky-product-details')) {
  customElements.define('sticky-product-details', StickyProductDetails);
}
